import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { buttonVariants, type ButtonVariant } from './button';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible name. Required: an icon-only control has no other label. */
  label: string;
  variant?: Extract<ButtonVariant, 'primary' | 'secondary' | 'ghost' | 'danger'>;
  size?: 'sm' | 'md' | 'lg';
}

// Keep the visual language compact on desktop, but give coarse-pointer users a
// real hit area instead of asking them to land on a 32 px icon.
const SQUARE = {
  sm: 'size-11 lg:size-8',
  md: 'size-11 lg:size-9',
  lg: 'size-11 lg:size-10',
} as const;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { label, className, variant = 'ghost', size = 'md', type = 'button', children, ...props },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(buttonVariants({ variant, size }), 'px-0', SQUARE[size], className)}
      {...props}
    >
      {children}
    </button>
  )
);

IconButton.displayName = 'IconButton';
